/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */
import * as React from 'react';

/*
 * Used to get access to router context.
 */
import { Link, useHistory, useParams } from 'react-router-dom';

/*
 * Used to style components
 */
import { Box, Button, CircularProgress, Dialog, DialogContent, Grid, Input, InputProps, makeStyles, Paper, TextField, useTheme } from '@material-ui/core';

/**
 * Used for the basic page layout.
 */
import { faUpload } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AlertTitle } from '@material-ui/lab';
import {
    ALL_INSTITUTIONS_CODE,
    CollapsibleTable,
    InstitutionBreadcrumbs,
    InstitutionsContext,
    IPatient,
    OnlinePatientManagementContext,
    PageLayout,
    PatientsByCodesResolver,
    PatientsContext,
    TrialContextSelector,
    useLookupsByFormDefinitionId,
    usePatientsByCodes,
    useSnackbar
} from '@ngt/opms';
import { RequestState } from '@ngt/request-utilities';
import { DateTime } from 'luxon';
import { Column } from 'material-table';
import { FinanceMilestone, FinanceMilestoneWorkbookPostImport, FormDefinitionType, Patient, PatientStateType } from '../api/dtos';
import ScreenFailuresResolver from '../components/resolver/ScreenFailuresResolver';
import ScreenFailuresContext from '../contexts/data/ScreenFailuresContext';
import useRandomisedPatientColumns from '../hooks/useRandomisedPatientColumns';
import useScreenFailuresColumns from '../hooks/useScreenFailuresColumns';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */



/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IRegistrationParams {
    masterGroupCode?: string
    collaboratingGroupCode?: string
    countryCode?: string
    institutionCode?: string
}

interface IRegistrationProps {
    canAdministerOpms?: boolean
}

interface IPatientTableProps {
    title: string;
    entityName: string;
    columns: Array<Column<Patient>>;
    onRowClick?: (event: any, rowData: any) => void,
    centralLab?: boolean;
}

interface IScreenFailuresTableProps {
    title: string;
    entityName: string;
}

const tableOptions = {
    pageSize: 20,
    pageSizeOptions: [20],
};

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    addPatientColumn: {
        display: 'flex',
        alignItems: 'flex-end'
    },
    addPatient: {
        width: '100%'
    },
    search: {
        display: 'flex',
        alignItems: 'flex-end',
        paddingRight: '12px',
        paddingTop: '12px',
        paddingBottom: '12px',
        gap: 24
    },
    financeMilestonesImportButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: '12px',
        paddingTop: '12px',
        paddingBottom: '12px',
        gap: 24
    },
    importContainer: {
            display: "flex",
            justifyContent: "center",
            width: "100%"
    },
    importLoading: {
        display: "flex",
        justifyContent: "center",
        width: "60px"
    },
    importParticipant: {
        padding: theme.spacing(2, 0),

    },
    importRowEven: {
        backgroundColor: theme.palette.grey[300]
    },
    appearAtSmall: {
        [theme.breakpoints.down('sm')]: {
            display: "none"
        },
        
        [theme.breakpoints.up('sm')]: {
            display: "grid"
        },
    }
}));

const MemoizedCollapsibleTable = React.memo(CollapsibleTable);

const PatientTable: React.FunctionComponent<IPatientTableProps> = ({
    title,
    entityName,
    columns,
    onRowClick,
    centralLab
}) => {
    const [tableCount, setTableCount] = React.useState(1);

    const { patients: contextPatients, loadState: patientsLoadState } = React.useContext(PatientsContext);

    const [patientsToUse, setPatientsToUse] = React.useState<Patient[]>([]);

    const patientsLoadingToUse = patientsLoadState.state === RequestState.None || patientsLoadState.state === RequestState.Pending;

    React.useEffect(() => {
        setPatientsToUse((contextPatients as Patient[])?.filter(p => !centralLab || p.studyNumber)) // doesnt work if i remove it so it filters them all?... i dunno
    }, [centralLab, contextPatients, setPatientsToUse]);

    React.useEffect(() => {
        setTableCount(x => x + 1);
    }, [patientsToUse, columns]);

    return (
        <MemoizedCollapsibleTable
            key={tableCount}
            data={patientsToUse ?? []}
            title={title ?? "Participants"}
            loading={patientsLoadingToUse}
            entityName={entityName ?? "Participant"}
            columns={columns as any}
            onRowClick={onRowClick as any}
            options={tableOptions}
        />
    );
}

const registeredStates = [PatientStateType.RandomisedOnAbemaciclib, PatientStateType.RandomisedOffAbemaciclib, PatientStateType.RandomisedOnAbemaciclibNz];

const ScreenFailuresTable: React.FunctionComponent<IScreenFailuresTableProps> = ({
    title,
    entityName
}) => {

    const { institutionCode } = useParams<IRegistrationParams>();

    const [tableCount, setTableCount] = React.useState<number>(0);

    const { screenFailures: screenFailuresContext, loadState: screenFailuresLoadState } = React.useContext(ScreenFailuresContext);
    const { institutions } = React.useContext(InstitutionsContext);

    const screenFailuresLoadingToUse = screenFailuresLoadState.state === RequestState.None || screenFailuresLoadState.state === RequestState.Pending;

    const screenFailuresToUse = React.useMemo(() => {
        if (!institutionCode) {
            return screenFailuresContext;
        }
        else {
            var institution = institutions?.find(i => i.code == institutionCode);
            return screenFailuresContext?.filter(sf => sf.patient?.institutionId == institution?.id) ?? null;
        }
    }, [screenFailuresContext, institutions, institutionCode]);

    const screenFailureColumns = useScreenFailuresColumns(screenFailuresToUse, institutions, institutionCode !== ALL_INSTITUTIONS_CODE);

    React.useEffect(() => {
        setTableCount(screenFailuresToUse?.length ?? 0);
    }, [screenFailuresToUse]);

    return (
        <MemoizedCollapsibleTable
            key={tableCount}
            data={screenFailuresToUse ?? []}
            title={title ?? "Ineligible Patients"}
            loading={screenFailuresLoadingToUse}
            entityName={entityName ?? "Participant"}
            columns={screenFailureColumns as any}
            //onRowClick={onRowClick as any}
            options={tableOptions}
        />
    );
}

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const Search: React.FunctionComponent<{}> = () => {

    const history = useHistory();


    const [patients] = usePatientsByCodes(null, null, null, null, null, true);

    const [searchValue, setSearchValue] = React.useState(null);

    const { enqueueSnackbar } = useSnackbar();

    const saveText = React.useCallback((e) => {
        setSearchValue(e.target.value);
    }, [searchValue, setSearchValue]);

    const search = React.useCallback(() => {
        if (patients?.some(p => p.studyNumber === searchValue)) {
            history.push(`/registration/all-institutions/` + searchValue + `/registration/1/registration-form/1`);
        }
        else {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Patient Not Found
                    </AlertTitle>
                    {searchValue ? <>Patient '{searchValue}' not found. Please ensure you have correctly input the patients study number.</> : <>The search field is empty.</>}
                </>,
                { variant: 'error' }
            );
        }
    }, [searchValue]);

    return (
        <>

            <TextField
                variant="standard"
                label="Patient Study Number"
                id='patientNumberSearch'
                onChange={saveText}
                style={{
                    flex: '1 1 auto',
                    width: '60%'
                }}
            />
            <Button
                variant='contained'
                onClick={search}

                style={{
                    flex: '1 1 auto',
                    width: '40%'
                }}
            >
                Search
            </Button>
        </>
    )
}

interface IFileInputProps extends Omit<InputProps, "type"> {

};

const FileInput: React.FunctionComponent<IFileInputProps> = ({
    style,
    className,
    inputRef,
    onChange,
    disabled,
    ...props
}) => {
    const [fileName, setFileName] = React.useState("No file selected.");

    const inputRefToUse = inputRef as any ?? React.useRef<any>(null);

    const onChangeCombined = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if(onChange) {
            onChange(e); 
        }
        
        setFileName(inputRefToUse.current?.files?.[0]?.name ?? "No file selected.");
    }
    return (
        <Box
            style={{
                display: "flex",
                alignItems: "center",
                maxWidth: "100%",
            }}
        >
            <Input {...props} inputRef={inputRefToUse} type={"file"} style={{display: "none"}} onChange={onChangeCombined} />
            <Box>
                <Button style={style} className={className} onClick={() => inputRefToUse.current?.click?.()} disabled={disabled}><FontAwesomeIcon icon={faUpload} /></Button>
            </Box>
            <Box
                style={{
                    textOverflow: "ellipsis",
                    overflow: "hidden"
                }}
            >
                {fileName}
            </Box>
        </Box>
    );
}

const FinanceMilestonesImportButton: React.FunctionComponent = () => {
    const { serviceStackClient: client } = React.useContext(OnlinePatientManagementContext);
    const inputRef = React.useRef<any>(null);
    const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
    const [results, setResults] = React.useState<FinanceMilestone[] | undefined>(undefined);
    const lookups = useLookupsByFormDefinitionId(FormDefinitionType.FinanceMilestone, true)[0];
    const patients = usePatientsByCodes(null, null, null, null, null, true)[0];
    const [loading, setLoading] = React.useState(false);
    const theme = useTheme();
    const styles = useStyles()

    const upload = React.useCallback(async () => {
        const file = inputRef.current?.files?.[0];

        if(!file) {
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        setLoading(true);
        let response = undefined;
        try {
            response = await client.apiForm(new FinanceMilestoneWorkbookPostImport(), formData, undefined, "post");
        }
        catch (e) {
            setLoading(false);
        }
        setLoading(false);
        setResults(() => response?.response?.financeMilestones);
    }, [inputRef.current?.files, client])

    const milestoneNames = React.useMemo(() => lookups?.find(lu => lu.propertyName == "MilestoneId")?.items ?? [], [lookups]);
    const getMilestoneName = React.useCallback((milestoneId?: number) => milestoneNames.find(item => item.id == milestoneId)?.value ?? "Unknown", [milestoneNames])

    const groupedResults = React.useMemo(() => (
        results?.reduce<Record<string, FinanceMilestone[]>>((state, current) => {
            const studyNumber = patients?.find(p => p.id == current?.patientId)?.studyNumber ?? "Unknown";
            if(!Object.keys(state).includes(studyNumber)) {
                state[studyNumber] = [];
            }
            state[studyNumber].push(current);

            return state;
        },{})
    ), [results, patients]);

    const resultGrid = React.useMemo(() => (
        groupedResults !== undefined &&  Object.keys(groupedResults).length > 0 ?
        <Grid
            container
        >
            The following milestones were successfully added: 
            {
                Object.keys(groupedResults).map((key, i) => 
                    <Grid
                        key={i}
                        container
                        spacing={2}
                        className={styles.importParticipant}
                    >
                        <Grid 
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            lg={12}
                            xl={12}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                fontSize: "1rem",
                                color: theme.palette.primary.light
                            }}
                        >
                            Participant - {key}
                        </Grid>
                        {
                            groupedResults[key].map((result, i) => (
                                <Grid 
                                    key={i}
                                    container
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}
                                    className={i%2 == 0 ? styles.importRowEven : undefined}
                                >
                                    <Grid>
                                        {getMilestoneName(result.milestoneId)}
                                    </Grid>
                                    <Grid>
                                        {result.milestoneDate ? DateTime.fromISO(result.milestoneDate).toFormat("dd/MM/yyyy") : "Unknown"}
                                    </Grid>
                                </Grid>
                            ))
                        }
                    </Grid>
                )
            }
        </Grid> : null
    ), [groupedResults]);

    return (
        <>
            <Box
                style={{
                    flex: '1 1 auto',
                    width: '60%'
                }}
            >

            </Box>
            <Button 
                variant="contained" 
                onClick={() => setUploadDialogOpen(true)}
                style={{
                    flex: '1 1 auto',
                    width: '40%'
                }}
            >
                Import Finance Milestones
            </Button>
            <Dialog
                open={uploadDialogOpen}
                onBackdropClick={() => setUploadDialogOpen(false)}
            >
                <DialogContent>
                    <Grid 
                        className={styles.importContainer}
                    >
                        <FileInput disabled={loading} inputRef={inputRef} inputProps={{accept: ".xls,.xlsx"}} />
                    </Grid>
                    
                    <Grid 
                        className={styles.importContainer}
                    >
                        <Button onClick={upload} disabled={loading}>Import</Button>
                    </Grid>
                    <Grid 
                        className={styles.importContainer}
                        container
                    >
                        {
                            loading ? <Grid item xs={1}><CircularProgress /></Grid> : 
                            resultGrid ? resultGrid : 
                            results ? <>No data was imported.</> :
                            null
                        }
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    )
}

const RegistrationPage: React.FunctionComponent<IRegistrationProps> = ({
    canAdministerOpms
}) => {

    const classes = useStyles();

    const history = useHistory();

    const { masterGroupCode, collaboratingGroupCode, countryCode, institutionCode } = useParams<IRegistrationParams>();

    const onSelect = React.useCallback((_newMasterGroupCode?: string | null, _newCollaboratingGroupCode?: string | null, _newCountryCode?: string | null, newInstitutionCode?: string | null) => {
        if (newInstitutionCode) {
            history.push(`/registration/${newInstitutionCode}`);
        }
        else {
            history.push(`/registration`);
        }

        return;
    }, [history]);

    const onRowClick = React.useCallback((_: React.MouseEvent<Element, MouseEvent>, rowData?: IPatient | undefined) => {
        history.push(`/registration/${institutionCode ?? ALL_INSTITUTIONS_CODE}/${rowData?.studyNumber}/registration/1/registration-form/1`)
    }, [history, institutionCode]);

    const { institutions } = React.useContext(InstitutionsContext);

    const institutionsToUse = React.useMemo(() => {
        return institutions?.filter(i => i.code);
    }, [institutions]);

    const parsedInstitutionCode = React.useMemo(() => {
        return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : null;
    }, [institutionCode]);

    const showInstitutions = React.useMemo(() => {
        return !parsedInstitutionCode
    }, [parsedInstitutionCode]);

    const randomisedColumns = useRandomisedPatientColumns(institutions, showInstitutions);

    return (
        <PageLayout
            breadcrumbs={<InstitutionBreadcrumbs />}
            heading="Registration"
        >
            <Paper
                className={classes.container}
            >
                <TrialContextSelector
                    onChange={onSelect}
                    allowAllMasterGroups={true}
                    allowAllCollaboratingGroups={true}
                    allowAllCountries={true}
                    allowAllInstitutions={true}
                    hideMasterGroups={true}
                    hideCollaboratingGroups={true}
                    hideCountries={true}
                    masterGroupCode={masterGroupCode}
                    collaboratingGroupCode={collaboratingGroupCode}
                    countryCode={countryCode}
                    institutionCode={institutionCode}
                    institutions={institutionsToUse}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        className={classes.search}
                    >
                        <Search />
                    </Grid>
                    {
                        canAdministerOpms &&
                        <>
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                className={classes.appearAtSmall}
                            />
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                className={classes.financeMilestonesImportButton}
                            >
                                <FinanceMilestonesImportButton />
                            </Grid>
                        </>
                    }
                    {
                        !showInstitutions && (
                            <Box
                                style={{ marginLeft: 'auto', alignSelf: 'center', marginRight: '12px', marginBottom: '6px' }}
                            >
                                {/* <Button
                                    style={{ marginRight: '12px' }}
                                    variant="contained"
                                    color="secondary"
                                >
                                    Import
                                </Button> */}
                                <Button
                                    style={{ width: '223px' }}
                                    variant="contained"
                                    color="primary"
                                    component={Link}
                                    to={`/registration/${parsedInstitutionCode}/add-participant`}
                                    fullWidth
                                >
                                    Add Patient
                                </Button>
                            </Box>
                        )
                    }
                </TrialContextSelector>

                <PatientsByCodesResolver
                    masterGroupCode={null}
                    collaboratingGroupCode={null}
                    countryCode={null}
                    institutionCode={parsedInstitutionCode}
                    patientStateIds={registeredStates}
                >
                    <PatientTable
                        title="Randomised Participants"
                        entityName="Participant"
                        onRowClick={onRowClick}
                        columns={randomisedColumns}
                        centralLab={false}
                    />
                </PatientsByCodesResolver>
                <ScreenFailuresResolver>
                    <ScreenFailuresTable
                        title="Ineligible Participants"
                        entityName="Participant"
                    />
                </ScreenFailuresResolver>
            </Paper>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default RegistrationPage;
